import { useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../libraries/clipboardUtils";
import { createEmployeeRequest, emptyApprovals, fetchEmployeeRequests, normalizeApprovals, updateEmployeeRequest } from "../../libraries/employeeRequests";
import {
  getCurrentEmployee,
  getEmployeeLoginId,
  getEmployeeName,
  getEmployeeRecordId,
  getLocalDateKey
} from "../../libraries/tecgymAdminAttendance";

const LEAVE_TYPES = ["Sick Leave", "Unpaid Leave", "Emergency Leave"];

const emptyForm = {
  leaveType: "Sick Leave",
  fromDate: "",
  toDate: "",
  reason: ""
};

const SHORT_LEAVE_APPROVERS = [
  { id: "masood", label: "Masood" },
  { id: "shanzay", label: "Shanzay" }
];

const LONG_LEAVE_APPROVER = { id: "ceo", label: "C.E.O" };

function normalizePersonKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z]/g, "");
}

function getLoggedInApproverId(name) {
  const key = normalizePersonKey(name);

  if (key.includes("masood")) {
    return "masood";
  }

  if (key.includes("shanzay")) {
    return "shanzay";
  }

  if ((key.includes("rashid") && key.includes("ebad")) || key.includes("ceo")) {
    return "ceo";
  }

  return "";
}

function getLeaveApprovers(request) {
  if (isMasoodOrShanzayRequest(request)) {
    return [LONG_LEAVE_APPROVER];
  }

  return Number(request?.dayCount) > 2 ? [LONG_LEAVE_APPROVER] : SHORT_LEAVE_APPROVERS;
}

function approverStatusLabel(approver, status) {
  if (approver.id === "ceo") {
    if (status === "Rejected") {
      return "C.E.O Rejected";
    }
    if (status === "Approved") {
      return "Rashid Ebad Approved";
    }
    return "Rashid Ebad Pending";
  }

  if (status === "Rejected") {
    return `${approver.label} Rejected`;
  }
  if (status === "Approved") {
    return `${approver.label} Approved`;
  }
  return `${approver.label} Pending`;
}

function approvalEntry(request, approverId) {
  return request.approvals?.[approverId] || { status: "Pending", message: "" };
}

function canActOnRequest(loggedInName, request) {
  const approverId = getLoggedInApproverId(loggedInName);
  if (!approverId) {
    return false;
  }

  return getLeaveApprovers(request).some((approver) => approver.id === approverId);
}

function requestOutcome(request) {
  const approvers = getLeaveApprovers(request);
  const statuses = approvers.map((approver) => approvalEntry(request, approver.id).status);

  if (statuses.some((status) => status === "Rejected")) {
    return "Rejected";
  }

  if (approvers.length > 0 && statuses.every((status) => status === "Approved")) {
    return "Approved";
  }

  return "Pending";
}

function isMasoodOrShanzayRequest(request) {
  const name = normalizePersonKey(request?.employeeName);
  const email = normalizePersonKey(request?.employeeEmail);
  return name.includes("masood") || name.includes("shanzay") || email.includes("masood") || email.includes("shanzay");
}

function isOwnRequest(request, employee) {
  const employeeId = getEmployeeRecordId(employee);
  const employeeEmail = getEmployeeLoginId(employee);
  const employeeName = normalizePersonKey(getEmployeeName(employee));

  if (employeeId && request.employeeId && request.employeeId === employeeId) {
    return true;
  }

  if (employeeEmail && request.employeeEmail && request.employeeEmail === employeeEmail) {
    return true;
  }

  return Boolean(employeeName) && normalizePersonKey(request.employeeName) === employeeName;
}

function countInclusiveDays(fromDate, toDate) {
  if (!fromDate || !toDate || toDate < fromDate) {
    return 0;
  }

  const start = new Date(`${fromDate}T00:00:00`);
  const end = new Date(`${toDate}T00:00:00`);
  const milliseconds = end.getTime() - start.getTime();
  return Math.floor(milliseconds / 86400000) + 1;
}

function ApplyRequestPanel({ mode = "leave", audience = "own" }) {
  const isLeave = mode === "leave";
  const isReview = audience === "others";
  const statusLabel = isLeave ? "Leave Status" : "Work From Home Status";
  const employee = useMemo(() => getCurrentEmployee(), []);
  const employeeName = useMemo(() => getEmployeeName(employee), [employee]);
  const canApprove = isReview && Boolean(getLoggedInApproverId(employeeName));
  const showEmployeeFilter = isReview && normalizePersonKey(employeeName).includes("rashid") && normalizePersonKey(employeeName).includes("ebad");
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [view, setView] = useState(isReview ? "status" : "apply");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const requestsRef = useRef([]);
  const [form, setForm] = useState(() => ({
    ...emptyForm,
    fromDate: getLocalDateKey(),
    toDate: getLocalDateKey()
  }));
  const [error, setError] = useState("");
  const dayCount = countInclusiveDays(form.fromDate, form.toDate);
  const visibleRequests = useMemo(
    () => requests.filter((request) => {
      if (!isReview) {
        return isOwnRequest(request, employee);
      }

      if (!isLeave && isMasoodOrShanzayRequest(request)) {
        return true;
      }

      return !isOwnRequest(request, employee);
    }),
    [employee, isLeave, isReview, requests]
  );
  const employeeOptions = useMemo(() => {
    const options = new Map();
    visibleRequests.forEach((request) => {
      const id = request.employeeId || normalizePersonKey(request.employeeName) || request.employeeEmail;
      if (!id || options.has(id)) {
        return;
      }
      options.set(id, request.employeeName || request.employeeEmail || "Employee");
    });

    return [...options.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((first, second) => first.name.localeCompare(second.name));
  }, [visibleRequests]);
  const activeEmployee = employeeOptions.some((option) => option.id === selectedEmployee)
    ? selectedEmployee
    : "all";
  const filteredRequests = useMemo(() => {
    if (!showEmployeeFilter || activeEmployee === "all") {
      return visibleRequests;
    }

    return visibleRequests.filter((request) => {
      const id = request.employeeId || normalizePersonKey(request.employeeName) || request.employeeEmail;
      return id === activeEmployee;
    });
  }, [activeEmployee, showEmployeeFilter, visibleRequests]);

  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");

    fetchEmployeeRequests(mode)
      .then((records) => {
        if (!cancelled) {
          setRequests(records);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(error.message || "Unable to load requests.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mode]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setError("");
  };

  const replaceRequest = (savedRequest) => {
    setRequests((current) => {
      const next = current.some((request) => request.id === savedRequest.id)
        ? current.map((request) => (request.id === savedRequest.id ? savedRequest : request))
        : [savedRequest, ...current];
      requestsRef.current = next;
      return next;
    });
  };

  const persistRequest = async (request) => {
    const savedRequest = await updateEmployeeRequest(mode, request);
    replaceRequest(savedRequest);
    return savedRequest;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    if (!form.fromDate || !form.toDate) {
      setError("Choose both a start date and an end date.");
      return;
    }

    if (form.toDate < form.fromDate) {
      setError("The end date must be on or after the start date.");
      return;
    }

    if (!form.reason.trim()) {
      setError(isLeave ? "Add a reason for this leave." : "Add a reason for working from home.");
      return;
    }

    const draft = {
      employeeId: getEmployeeRecordId(employee),
      employeeName,
      employeeEmail: getEmployeeLoginId(employee),
      leaveType: form.leaveType,
      fromDate: form.fromDate,
      toDate: form.toDate,
      dayCount,
      reason: form.reason.trim(),
      status: "Pending",
      decisions: {},
      rejectReasons: {},
      approvals: emptyApprovals()
    };

    try {
      setSubmitting(true);
      const savedRequest = await createEmployeeRequest(mode, draft);
      replaceRequest(savedRequest);
      setForm({
        ...emptyForm,
        fromDate: getLocalDateKey(),
        toDate: getLocalDateKey()
      });
      setView("status");
      showToast(
        isLeave ? "Leave request saved." : "Work from home request saved.",
        "success"
      );
    } catch (error) {
      showToast(error.message || "Unable to save the request.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (requestId, approverId, decision, rejectReason = "") => {
    const currentRequest = requestsRef.current.find((request) => request.id === requestId);
    if (!currentRequest) {
      return;
    }

    const allowedApprover = getLeaveApprovers(currentRequest).some((approver) => approver.id === approverId)
      && getLoggedInApproverId(employeeName) === approverId;
    if (!allowedApprover) {
      showToast(
        isMasoodOrShanzayRequest(currentRequest)
          ? "Only the C.E.O can approve this request."
          : "You cannot approve this request.",
        "error"
      );
      return;
    }

    const approvals = normalizeApprovals(
      currentRequest.approvals,
      currentRequest.decisions,
      currentRequest.rejectReasons
    );
    approvals[approverId] = {
      status: decision === "Reject" ? "Rejected" : "Approved",
      message: decision === "Reject" ? rejectReason : ""
    };

    const nextRequest = {
      ...currentRequest,
      approvals
    };
    nextRequest.status = requestOutcome(nextRequest);

    try {
      const savedRequest = await persistRequest(nextRequest);
      replaceRequest(savedRequest);
      showToast(
        decision === "Approved" ? "Approval saved." : "Rejection saved.",
        "success"
      );
    } catch (error) {
      showToast(error.message || "Unable to save the decision.", "error");
      throw error;
    }
  };

  const handleReset = () => {
    setForm({
      ...emptyForm,
      fromDate: getLocalDateKey(),
      toDate: getLocalDateKey()
    });
    setError("");
  };

  return (
    <section style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <div>
          <h2 style={sectionTitleStyle}>
            {isReview
              ? (isLeave ? "Leave Requests" : "Work From Home Requests")
              : (isLeave ? "Apply Leave" : "Apply Work From Home")}
          </h2>
          <p style={sectionSubtitleStyle}>
            {isReview
              ? (isLeave
                ? "Other employees' leave requests, shown as Approved or Rejected."
                : "Other employees' work from home requests, shown as Approved or Rejected.")
              : (view === "apply"
                ? (isLeave
                  ? "Fill in the leave dates and reason."
                  : "Fill in the work from home dates and reason.")
                : (isLeave
                  ? "Your saved leave requests."
                  : "Your saved work from home requests."))}
          </p>
        </div>
      </div>

      {!isReview && <div style={subTabRowStyle}>
        <button
          type="button"
          onClick={() => setView("apply")}
          style={view === "apply" ? activeSubTabStyle : subTabStyle}
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => setView("status")}
          style={view === "status" ? activeSubTabStyle : subTabStyle}
        >
          {statusLabel}
        </button>
      </div>}

      {showEmployeeFilter && !loading && !loadError && (
        <label style={employeeFilterStyle}>
          Employee
          <select
            value={activeEmployee}
            onChange={(event) => setSelectedEmployee(event.target.value)}
            style={employeeFilterSelectStyle}
          >
            <option value="all">All employees</option>
            {employeeOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
        </label>
      )}

      {isReview || view === "status" ? (
        loading ? (
          <div style={emptyStateStyle}>{isLeave ? "Loading leave requests..." : "Loading work from home requests..."}</div>
        ) : loadError ? (
          <div style={formErrorStyle}>{loadError}</div>
        ) : (
          <RequestStatusTable
            isLeave={isLeave}
            requests={filteredRequests}
            emptyLabel={isReview
              ? (activeEmployee !== "all"
                ? (isLeave ? "No leave requests for this employee." : "No work from home requests for this employee.")
                : (isLeave ? "No other leave requests." : "No other work from home requests."))
              : (isLeave ? "No leave requests yet." : "No work from home requests yet.")}
            loggedInName={employeeName}
            canApprove={canApprove}
            onDecision={handleDecision}
          />
        )
      ) : (
      <form onSubmit={handleSubmit} style={formLayoutStyle}>
        <label style={fieldLabelStyle}>
          Employee
          <input value={employeeName} readOnly style={readOnlyInputStyle} />
        </label>

        {isLeave && (
          <label style={fieldLabelStyle}>
            Leave type
            <select value={form.leaveType} onChange={updateField("leaveType")} style={formInputStyle}>
              {LEAVE_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </label>
        )}

        <div style={dateRowStyle}>
          <label style={fieldLabelStyle}>
            From
            <input type="date" value={form.fromDate} onChange={updateField("fromDate")} style={formInputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            To
            <input type="date" value={form.toDate} onChange={updateField("toDate")} style={formInputStyle} />
          </label>
        </div>

        <div style={durationStyle}>
          {dayCount > 0
            ? `${dayCount} day${dayCount === 1 ? "" : "s"}`
            : "Choose a valid date range"}
        </div>

        <label style={fieldLabelStyle}>
          Reason
          <textarea
            value={form.reason}
            onChange={updateField("reason")}
            rows={4}
            placeholder={isLeave ? "Why do you need this leave?" : "Why do you need to work from home?"}
            style={textareaStyle}
          />
        </label>

        {error && <div style={formErrorStyle}>{error}</div>}

        <div style={actionsStyle}>
          <button type="button" style={secondaryButtonStyle} onClick={handleReset}>
            Clear
          </button>
          <button type="submit" style={submitting ? disabledButtonStyle : primaryButtonStyle} disabled={submitting}>
            {submitting ? "Saving..." : (isLeave ? "Apply Leave" : "Apply Work From Home")}
          </button>
        </div>
      </form>
      )}
    </section>
  );
}

function RequestStatusTable({ isLeave, requests, emptyLabel, loggedInName, canApprove, onDecision }) {
  const [popup, setPopup] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [savingDecision, setSavingDecision] = useState(false);

  if (requests.length === 0) {
    return <div style={emptyStateStyle}>{emptyLabel}</div>;
  }

  const openPopup = (nextPopup) => {
    setShowRejectForm(false);
    setRejectReason("");
    setRejectError("");
    setPopup(nextPopup);
  };

  const closePopup = () => {
    if (savingDecision) {
      return;
    }

    setPopup(null);
  };

  const saveApproval = async () => {
    if (!popup || savingDecision) {
      return;
    }

    try {
      setSavingDecision(true);
      await onDecision(popup.requestId, popup.approver.id, "Approved");
      setPopup(null);
    } catch {
      // The panel already shows the save error.
    } finally {
      setSavingDecision(false);
    }
  };

  const saveRejection = async () => {
    if (!popup || savingDecision) {
      return;
    }

    if (!rejectReason.trim()) {
      setRejectError(isLeave ? "Add a reason for rejecting this leave." : "Add a reason for rejecting this work from home.");
      return;
    }

    try {
      setSavingDecision(true);
      await onDecision(popup.requestId, popup.approver.id, "Reject", rejectReason.trim());
      setPopup(null);
    } catch {
      // The panel already shows the save error.
    } finally {
      setSavingDecision(false);
    }
  };

  return (
    <>
      <div style={tableShellStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Employee</th>
              {isLeave && <th style={tableHeaderStyle}>Leave type</th>}
              <th style={tableHeaderStyle}>From</th>
              <th style={tableHeaderStyle}>To</th>
              <th style={tableHeaderStyle}>Days</th>
              <th style={tableHeaderStyle}>Reason</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Approval</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id} style={tableRowStyle}>
                <td style={tableCellStyle}>{request.employeeName}</td>
                {isLeave && <td style={tableCellStyle}>{request.leaveType}</td>}
                <td style={tableCellStyle}>{request.fromDate}</td>
                <td style={tableCellStyle}>{request.toDate}</td>
                <td style={tableCellStyle}>{request.dayCount}</td>
                <td style={tableCellStyle}>{request.reason}</td>
                <td style={tableCellStyle}>
                  <span style={outcomeBadgeStyle(requestOutcome(request))}>{requestOutcome(request)}</span>
                </td>
                <td style={tableCellStyle}>
                  <LeaveApprovalActions
                    request={request}
                    loggedInName={loggedInName}
                    onOpen={canApprove && canActOnRequest(loggedInName, request) ? openPopup : null}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {popup && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label={`${popup.approver.label} decision`}
          onClick={closePopup}
        >
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <h3 style={modalTitleStyle}>{popup.approver.label}</h3>
            <p style={modalTextStyle}>
              {isLeave
                ? "Choose Approved or Reject for this leave."
                : "Choose Approved or Reject for this work from home."}
            </p>
            <div style={popupActionsStyle}>
              <button type="button" style={approveChoiceStyle} onClick={saveApproval} disabled={savingDecision}>
                {savingDecision && !showRejectForm ? "Saving..." : "Approved"}
              </button>
              <button
                type="button"
                style={rejectChoiceStyle}
                onClick={() => {
                  setShowRejectForm(true);
                  setRejectError("");
                }}
                disabled={savingDecision}
              >
                Reject
              </button>
            </div>
            {showRejectForm && (
              <label style={popupReasonLabelStyle}>
                Reason
                <textarea
                  value={rejectReason}
                  onChange={(event) => {
                    setRejectReason(event.target.value);
                    setRejectError("");
                  }}
                  placeholder={isLeave ? "Why is this leave rejected?" : "Why is this work from home rejected?"}
                  rows={4}
                  style={popupReasonInputStyle}
                />
                {rejectError && <div style={popupReasonErrorStyle}>{rejectError}</div>}
                <button type="button" style={popupSaveButtonStyle} onClick={saveRejection} disabled={savingDecision}>
                  {savingDecision ? "Saving..." : "Save"}
                </button>
              </label>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function LeaveApprovalActions({ request, loggedInName, onOpen }) {
  const [messagePopup, setMessagePopup] = useState(null);
  const approvers = getLeaveApprovers(request);
  const loggedInApproverId = getLoggedInApproverId(loggedInName);

  const handleStatusClick = (approver) => {
    const entry = approvalEntry(request, approver.id);
    if (!onOpen || loggedInApproverId !== approver.id || entry.status !== "Pending") {
      return;
    }

    onOpen({ requestId: request.id, approver });
  };

  return (
    <>
      <div style={approvalCellStyle}>
        {approvers.map((approver) => {
          const entry = approvalEntry(request, approver.id);
          const label = approverStatusLabel(approver, entry.status);
          const canDecide = Boolean(onOpen) && loggedInApproverId === approver.id && entry.status === "Pending";
          const statusStyle = entry.status === "Approved"
            ? approvedDecisionStyle
            : entry.status === "Rejected"
              ? rejectedDecisionStyle
              : pendingDecisionStyle;
          const showChat = entry.status === "Rejected" && Boolean(entry.message);

          return (
            <div key={approver.id} style={decisionBlockStyle}>
              {canDecide ? (
                <button type="button" style={{ ...statusStyle, cursor: "pointer" }} onClick={() => handleStatusClick(approver)}>
                  {label}
                </button>
              ) : (
                <div style={statusStyle}>{label}</div>
              )}
              {showChat && (
                <button
                  type="button"
                  style={chatButtonStyle}
                  aria-label={`${approver.label} rejection message`}
                  onClick={() => setMessagePopup({ title: `${approver.label} Rejected`, message: entry.message })}
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 1 1 18 0Z" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>
      {messagePopup && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label={messagePopup.title}
          onClick={() => setMessagePopup(null)}
        >
          <div style={messageModalCardStyle} onClick={(event) => event.stopPropagation()}>
            <h3 style={modalTitleStyle}>{messagePopup.title}</h3>
            <p style={messageTextStyle}>{String(messagePopup.message || "").replace(/\s+/g, " ").trim()}</p>
            <div style={popupActionsStyle}>
              <button type="button" style={approveChoiceStyle} onClick={() => setMessagePopup(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const sectionStyle = {
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
};

const sectionHeaderStyle = {
  marginBottom: "20px"
};

const sectionTitleStyle = {
  margin: "0 0 6px 0",
  fontSize: "24px",
  fontWeight: 800
};

const sectionSubtitleStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "14px"
};

const employeeFilterStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "min(320px, 100%)",
  marginBottom: "18px",
  color: "var(--home-heading, #111827)",
  fontSize: "14px",
  fontWeight: 800
};

const employeeFilterSelectStyle = {
  width: "100%",
  border: "1px solid var(--home-border, #d1d5db)",
  borderRadius: "10px",
  backgroundColor: "#fff",
  color: "#111827",
  padding: "12px 14px",
  fontSize: "14px",
  fontWeight: 700,
  outline: "none"
};

const subTabRowStyle = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  marginBottom: "20px"
};

const subTabStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  color: "var(--home-muted, #ccc)",
  borderRadius: "12px",
  padding: "12px 22px",
  fontSize: "15px",
  fontWeight: 700,
  cursor: "pointer"
};

const activeSubTabStyle = {
  ...subTabStyle,
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  borderColor: "#187718",
  color: "#fff"
};

const emptyStateStyle = {
  border: "1px dashed var(--home-border, #444)",
  borderRadius: "14px",
  padding: "28px 16px",
  textAlign: "center",
  color: "var(--home-muted, #888)",
  fontWeight: 700
};

const tableShellStyle = {
  width: "100%",
  overflowX: "auto"
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "720px"
};

const tableHeaderStyle = {
  textAlign: "left",
  padding: "12px 14px",
  borderBottom: "1px solid var(--home-border, #444)",
  color: "var(--home-muted, #888)",
  fontSize: "13px",
  fontWeight: 800,
  whiteSpace: "nowrap"
};

const tableRowStyle = {
  borderBottom: "1px solid var(--home-border, #444)"
};

const tableCellStyle = {
  padding: "14px",
  fontSize: "14px",
  verticalAlign: "top"
};

const statusBadgeStyle = {
  display: "inline-block",
  borderRadius: "999px",
  padding: "4px 10px",
  backgroundColor: "#fef3c7",
  color: "#92400e",
  fontSize: "12px",
  fontWeight: 800
};

const approvedBadgeStyle = {
  ...statusBadgeStyle,
  backgroundColor: "#dcfce7",
  color: "#14532d"
};

const rejectedBadgeStyle = {
  ...statusBadgeStyle,
  backgroundColor: "#fee2e2",
  color: "#7f1d1d"
};

function outcomeBadgeStyle(outcome) {
  if (outcome === "Approved") {
    return approvedBadgeStyle;
  }

  if (outcome === "Rejected") {
    return rejectedBadgeStyle;
  }

  return statusBadgeStyle;
}

const approvalCellStyle = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "8px",
  overflow: "visible"
};

const pendingDecisionStyle = {
  color: "#92400e",
  backgroundColor: "#fef3c7",
  border: "1px solid #f59e0b",
  borderRadius: "8px",
  padding: "6px 10px",
  fontSize: "13px",
  fontWeight: 800,
  whiteSpace: "nowrap"
};

const approvedDecisionStyle = {
  color: "#14532d",
  backgroundColor: "#dcfce7",
  border: "1px solid #86efac",
  borderRadius: "8px",
  padding: "6px 10px",
  fontSize: "13px",
  fontWeight: 800,
  whiteSpace: "nowrap"
};

const rejectedDecisionStyle = {
  color: "#7f1d1d",
  backgroundColor: "#fee2e2",
  border: "1px solid #fca5a5",
  borderRadius: "8px",
  padding: "6px 10px",
  fontSize: "13px",
  fontWeight: 800,
  whiteSpace: "nowrap"
};

const decisionBlockStyle = {
  display: "inline-flex",
  flexDirection: "row",
  alignItems: "center",
  flexShrink: 0,
  gap: "8px",
  overflow: "visible"
};

const chatButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: "44px",
  minWidth: "44px",
  height: "44px",
  minHeight: "44px",
  padding: 0,
  border: "1px solid #dc2626",
  borderRadius: "999px",
  backgroundColor: "#fff",
  color: "#b91c1c",
  cursor: "pointer",
  lineHeight: 0
};

const messageModalCardStyle = {
  width: "min(420px, calc(100vw - 48px))",
  maxHeight: "min(70vh, 480px)",
  overflowY: "auto",
  backgroundColor: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 24px 60px rgba(0, 0, 0, 0.35)",
  color: "#111827",
  whiteSpace: "normal"
};

const messageTextStyle = {
  margin: "0 0 18px 0",
  color: "#111827",
  fontSize: "22px",
  lineHeight: 1.6,
  whiteSpace: "nowrap",
  overflowX: "auto"
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  whiteSpace: "normal"
};

const modalCardStyle = {
  width: "min(100%, 360px)",
  backgroundColor: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 24px 60px rgba(0, 0, 0, 0.35)",
  color: "#111827"
};

const modalTitleStyle = {
  margin: "0 0 8px 0",
  fontSize: "22px",
  fontWeight: 800
};

const modalTextStyle = {
  margin: "0 0 18px 0",
  color: "#4b5563",
  fontSize: "14px"
};

const popupActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  flexWrap: "wrap"
};

const approveChoiceStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer"
};

const rejectChoiceStyle = {
  border: "1px solid #dc2626",
  backgroundColor: "#dc2626",
  color: "#fff",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer"
};

const popupReasonLabelStyle = {
  display: "block",
  marginTop: "18px",
  color: "#7f1d1d",
  fontSize: "14px",
  fontWeight: 800
};

const popupReasonInputStyle = {
  display: "block",
  width: "100%",
  marginTop: "8px",
  border: "1px solid #dc2626",
  borderRadius: "10px",
  backgroundColor: "#fff",
  color: "#111827",
  padding: "12px 14px",
  fontSize: "14px",
  fontFamily: "inherit",
  resize: "vertical",
  boxSizing: "border-box"
};

const popupReasonErrorStyle = {
  marginTop: "8px",
  color: "#7f1d1d",
  backgroundColor: "#fee2e2",
  border: "1px solid #dc2626",
  borderRadius: "8px",
  padding: "8px 10px",
  fontSize: "13px",
  fontWeight: 800
};

const popupSaveButtonStyle = {
  ...approveChoiceStyle,
  display: "block",
  marginTop: "12px",
  marginLeft: "auto"
};

const formLayoutStyle = {
  maxWidth: "640px"
};

const fieldLabelStyle = {
  display: "block",
  marginBottom: "16px",
  color: "var(--home-heading, #fff)",
  fontSize: "14px",
  fontWeight: 700
};

const formInputStyle = {
  display: "block",
  width: "100%",
  marginTop: "8px",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "10px",
  backgroundColor: "var(--home-page-bg, #1f232b)",
  color: "var(--home-heading, #fff)",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};

const readOnlyInputStyle = {
  ...formInputStyle,
  cursor: "default"
};

const textareaStyle = {
  ...formInputStyle,
  resize: "vertical",
  minHeight: "110px",
  fontFamily: "inherit"
};

const dateRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "16px"
};

const durationStyle = {
  margin: "-4px 0 16px 0",
  color: "#4ade80",
  fontSize: "14px",
  fontWeight: 800
};

const formErrorStyle = {
  color: "#7f1d1d",
  backgroundColor: "#fee2e2",
  border: "1px solid #dc2626",
  borderRadius: "10px",
  padding: "10px 12px",
  marginBottom: "16px",
  fontSize: "14px",
  fontWeight: 800
};

const actionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  flexWrap: "wrap"
};

const disabledButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "var(--home-border, #444)",
  color: "#fff",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "not-allowed"
};

const primaryButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const secondaryButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "transparent",
  color: "var(--home-heading, #fff)",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer"
};

export default ApplyRequestPanel;
